import { Icon, IconName } from "@/components/icons/Icon";
import { IconButton, IconButtonColor } from "@/components/interactive/IconButton";
import { RefreshButton } from "@/components/interactive/RefreshButton";
import { cx } from "@/utils/classnames";
import React, { useEffect, useState } from "react";

export interface MessageProps extends Omit<React.ComponentProps<"div">, "title"> {
  title?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  iconClassName?: string;
  compact?: boolean;
  // When using this, set the `visible` prop so that the component will reappear after being dismissed
  canBeDismissed?: boolean;
  refetch?: () => void;
  isRefetching?: boolean;
  visible?: boolean;
  type?: MessageType;
}

export enum MessageType {
  ERROR = "ERROR",
  INFO = "INFO",
  WARNING = "WARNING",
}

const TEXT_CLASS_FOR_TYPE: Record<MessageType, string> = {
  [MessageType.ERROR]: "text-red-700 dark:text-cds-status-red-text-dark",
  [MessageType.INFO]: "text-cds-bright-green-700 dark:text-cds-bright-green-300",
  [MessageType.WARNING]: "text-yellow-700 dark:text-cds-status-yellow-text-dark",
};

const CLASSNAME_FOR_TYPE = {
  [MessageType.ERROR]:
    "bg-red-50 border-red-100 text-red-700 " +
    "dark:bg-cds-status-red-fill-dark dark:text-cds-status-red-text-dark",
  [MessageType.INFO]:
    "bg-cds-bright-green-50 border-cds-bright-green-100 text-cds-bright-green-700 " +
    "dark:text-cds-bright-green-300",
  [MessageType.WARNING]:
    "bg-yellow-50 border-yellow-100 text-yellow-700 " +
    "dark:bg-cds-status-yellow-fill-dark dark:text-cds-status-yellow-text-dark",
} satisfies Record<MessageType, string>;

const ARIA_ROLE_FOR_TYPE = {
  [MessageType.ERROR]: "alert",
  [MessageType.INFO]: "status",
  [MessageType.WARNING]: "alert",
} satisfies Record<MessageType, string>;

const ICON_FOR_TYPE = {
  [MessageType.ERROR]: "warning",
  [MessageType.INFO]: "info",
  [MessageType.WARNING]: "warning",
} satisfies Record<MessageType, IconName>;

const BUTTON_COLOR_FOR_TYPE = {
  [MessageType.ERROR]: "danger",
  [MessageType.INFO]: "primary",
  [MessageType.WARNING]: "warning",
} satisfies Record<MessageType, IconButtonColor>;

export const Message = ({
  children,
  className,
  title,
  iconClassName,
  isRefetching,
  compact,
  refetch,
  canBeDismissed,
  visible = true,
  type = MessageType.INFO,
}: MessageProps) => {
  const hasHeader = title || refetch || canBeDismissed;
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    setIsDismissed(false);
  }, [visible]);

  if (!visible || (canBeDismissed && isDismissed)) {
    return null;
  }

  return (
    <div
      role={ARIA_ROLE_FOR_TYPE[type]}
      className={cx(
        "border rounded-sm",
        "flex min-w-0 max-w-full max-h-96 items-start overflow-auto px-4 text-base gap-2",
        compact ? "py-2" : "py-6",
        CLASSNAME_FOR_TYPE[type],
        className,
      )}
    >
      <Icon
        name={ICON_FOR_TYPE[type]}
        size={compact ? "md" : "lg"}
        className={cx(
          "mt-0.5",
          TEXT_CLASS_FOR_TYPE[type],
          iconClassName,
          compact && hasHeader ? "translate-y-[3px]" : "",
        )}
      />
      <div
        className={cx(
          "min-w-0 flex-grow flex flex-col [overflow-wrap:anywhere]",
          !compact && "gap-2",
        )}
      >
        {hasHeader ? (
          <div className={"flex justify-between gap-2"}>
            <div className={cx("text-base")}>{title}</div>
            {refetch != null && (
              <RefreshButton
                color={BUTTON_COLOR_FOR_TYPE[type]}
                refresh={refetch}
                loading={!!isRefetching}
                lastUpdated={undefined}
              />
            )}
            {canBeDismissed && (
              <IconButton
                iconName="close"
                color={BUTTON_COLOR_FOR_TYPE[type]}
                onClick={() => setIsDismissed(true)}
                size={"sm"}
              />
            )}
          </div>
        ) : null}
        {children && <div className={cx("text-cds-sm")}>{children}</div>}
      </div>
    </div>
  );
};

export function ErrorMessage(props: MessageProps) {
  return <Message type={MessageType.ERROR} {...props} />;
}

export function WarnMessage(props: MessageProps) {
  return <Message type={MessageType.WARNING} {...props} />;
}

export function InfoMessage(props: MessageProps) {
  return <Message type={MessageType.INFO} {...props} />;
}
